
import { useState, useEffect, useRef } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const processSteps = [
    {
      number: '01',
      title: 'ヒアリング',
      description: 'お客様のご要望や課題を丁寧にお伺いします',
      icon: 'ri-discuss-line',
      color: 'teal'
    },
    {
      number: '02',
      title: '企画・提案',
      description: '最適なソリューションをご提案いたします',
      icon: 'ri-lightbulb-line',
      color: 'orange'
    },
    {
      number: '03',
      title: '制作・開発',
      description: '最新技術で高品質な成果物を制作します',
      icon: 'ri-code-s-slash-line',
      color: 'green'
    },
    {
      number: '04',
      title: 'サポート',
      description: '納品後も安心のアフターサポート体制',
      icon: 'ri-customer-service-2-line',
      color: 'pink'
    }
  ];

  const features = [
    {
      icon: 'ri-lightbulb-line',
      title: '創造性',
      description: 'お客様のビジョンを形にする創造的なアプローチ'
    },
    {
      icon: 'ri-rocket-line',
      title: '最新技術',
      description: '常に最新のWeb技術とトレンドを取り入れた制作'
    },
    {
      icon: 'ri-heart-line',
      title: '地域密着',
      description: '青森県弘前市を中心とした地域に根ざしたサービス'
    },
    {
      icon: 'ri-shield-check-line',
      title: '信頼性',
      description: '確実な納期とアフターサポートで安心をお届け'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      teal: { bg: 'bg-teal-500', text: 'text-teal-500' },
      orange: { bg: 'bg-orange-500', text: 'text-orange-500' },
      green: { bg: 'bg-green-500', text: 'text-green-500' },
      pink: { bg: 'bg-pink-500', text: 'text-pink-500' }
    };
    return colors[color];
  };

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-gray-50 relative overflow-hidden">
      {/* くまチョップ（黒）コンテンツの奥にうっすら・右端から半身はみ出し */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 z-0 w-56 h-56 opacity-35 pointer-events-none select-none hidden lg:block" aria-hidden style={{ transform: 'translate(30%, -50%)' }}>
        <img src={`${import.meta.env.BASE_URL}kumachop-black.png`} alt="" className="w-full h-full object-contain object-center" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              会社概要
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              WEBデザインくまチョップは、青森県弘前市を拠点とするWebデザイン事業所です。
              お客様のビジネスを成功に導くため、最新の技術と創造性を駆使したWebソリューションを提供しています。
            </p>
            <p className="text-gray-600 mb-8 leading-relaxed">
              地域密着型のサービスを心がけ、お客様との密なコミュニケーションを通じて、
              ニーズに最適化されたオーダーメイドのWebサイトやデジタルコンテンツを制作いたします。
            </p>

            {/* Process Steps */}
            <div className="space-y-4 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">制作の流れ</h3>
              {processSteps.map((step, index) => {
                const colors = getColorClasses(step.color);
                return (
                  <div
                    key={index}
                    className={`relative flex items-start gap-4 transform transition-all duration-1000 ${
                      isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 ${colors.bg} flex items-center justify-center`}>
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className={`text-xs font-bold ${colors.text}`}>{step.number}</span>
                        <h4 className="text-base font-bold text-gray-900">{step.title}</h4>
                      </div>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>

                    {/* Arrow */}
                    {index < processSteps.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-4 bg-gray-300"></div>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-black hover:bg-gray-800 text-white px-8 py-4 font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </button>
          </div>

          {/* Right content */}
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/94b1f1bd4fea3de955c74d2ea2c388ec/f82438b04f859931982e92a8210dbc15.jpeg"
                alt="オフィス環境"
                className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="text-2xl font-bold text-teal-500 mb-1">青森県</div>
                <div className="text-gray-600">弘前市拠点</div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mt-12">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`bg-white p-6 rounded-xl shadow-md transform transition-all duration-1000 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${(index + 4) * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-2xl text-teal-500`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
